/**
 * Motowheeler global interactions
 */
(() => {
  const doc = document;
  const body = doc.body;

  /* Reveal on scroll — threshold 0 so tall product grids show as soon as any pixel enters view */
  const revealEls = doc.querySelectorAll('.reveal, .reveal-stagger');
  if (revealEls.length && 'IntersectionObserver' in window) {
    const markVisible = (el, io) => {
      el.classList.add('is-visible');
      if (io) io.unobserve(el);
    };
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) markVisible(entry.target, io);
        });
      },
      { rootMargin: '120px 0px 120px 0px', threshold: 0 }
    );
    revealEls.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const near =
        rect.top < window.innerHeight + 120 && rect.bottom > -80;
      if (near) {
        markVisible(el);
      } else {
        io.observe(el);
      }
    });
  } else {
    revealEls.forEach((el) => el.classList.add('is-visible'));
  }

  /* Drawer helpers */
  const closeDrawers = () => {
    doc.querySelectorAll('.drawer.is-open, .menu-drawer.is-open, .cart-drawer.is-open').forEach((el) => {
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
    });
    doc.querySelectorAll('.drawer-overlay.is-open').forEach((el) => el.classList.remove('is-open'));
    body.classList.remove('drawer-open');
  };

  const openDrawer = (id) => {
    const drawer = doc.getElementById(id);
    if (!drawer) return;
    closeDrawers();
    const overlay = doc.querySelector(`[data-drawer-overlay="${id}"]`) || doc.querySelector('.drawer-overlay');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('is-open');
    body.classList.add('drawer-open');
  };

  window.MotowheelerDrawer = { open: openDrawer, close: closeDrawers };

  /* Event delegation so cart drawer still works after AJAX refresh */
  doc.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-drawer-open]');
    if (openBtn) {
      e.preventDefault();
      openDrawer(openBtn.getAttribute('data-drawer-open'));
      return;
    }
    if (e.target.closest('[data-drawer-close]')) {
      e.preventDefault();
      closeDrawers();
      return;
    }
    if (e.target.closest('.drawer-overlay')) {
      closeDrawers();
    }
  });

  doc.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawers();
  });

  const shopRoot = () => (window.Shopify?.routes?.root ? window.Shopify.routes.root : '/');

  const refreshCartDrawer = async (keepOpen = true) => {
    try {
      const res = await fetch(`${shopRoot()}?sections=cart-drawer`);
      if (!res.ok) return;
      const data = await res.json();
      const html = data['cart-drawer'];
      if (!html) return;
      const temp = doc.createElement('div');
      temp.innerHTML = html;
      const newSection = temp.querySelector('#shopify-section-cart-drawer') || temp.firstElementChild;
      const oldSection = doc.getElementById('shopify-section-cart-drawer');
      if (newSection && oldSection) {
        oldSection.replaceWith(newSection);
      } else if (newSection) {
        const oldDrawer = doc.getElementById('CartDrawer');
        if (oldDrawer?.parentElement) oldDrawer.parentElement.replaceWith(newSection);
      }
      if (keepOpen) openDrawer('CartDrawer');
    } catch {
      /* ignore — count bubble still updates */
    }
  };

  const changeCartLine = async (key, quantity) => {
    const res = await fetch(`${shopRoot()}cart/change.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: key, quantity }),
    });
    const cart = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(cart.description || cart.message || 'Cart update failed');
    updateCartCount(cart.item_count || 0);
    await refreshCartDrawer(true);
    doc.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    return cart;
  };

  doc.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-cart-remove]');
    const qtyBtn = e.target.closest('[data-cart-qty-change]');
    if (!removeBtn && !qtyBtn) return;

    e.preventDefault();
    const key = (removeBtn || qtyBtn).getAttribute('data-line-key');
    if (!key) return;

    try {
      if (removeBtn) {
        await changeCartLine(key, 0);
        return;
      }
      const delta = Number(qtyBtn.getAttribute('data-cart-qty-change') || 0);
      const row = qtyBtn.closest('[data-cart-line]');
      const current = Number(row?.querySelector('[data-cart-qty-value]')?.textContent || 1);
      const next = Math.max(0, current + delta);
      await changeCartLine(key, next);
    } catch (err) {
      console.warn(err);
    }
  });
  /* Mobile nav accordion */
  doc.querySelectorAll('[data-submenu-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      const panel = btn.nextElementSibling;
      if (panel) panel.hidden = expanded;
    });
  });

  /* Sticky header shadow */
  const header = doc.querySelector('.site-header');
  if (header) {
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* Cart count bubble + bump */
  const bumpCartUi = () => {
    const btn = doc.querySelector('.site-header__cart-btn');
    if (btn) {
      btn.classList.remove('is-bump');
      void btn.offsetWidth;
      btn.classList.add('is-bump');
    }
    doc.querySelectorAll('.site-header__cart-btn [data-cart-count]').forEach((el) => {
      el.classList.remove('is-pop');
      void el.offsetWidth;
      el.classList.add('is-pop');
    });
  };

  const showBagToast = (label) => {
    let host = doc.querySelector('[data-bag-toast]');
    if (!host) {
      host = doc.createElement('div');
      host.className = 'mw-bag-toast';
      host.setAttribute('data-bag-toast', '');
      host.setAttribute('role', 'status');
      doc.body.appendChild(host);
    }
    host.innerHTML = `<div class="mw-bag-toast__card">
      <svg class="mw-bag-toast__check" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 13l4 4L19 7" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${label || 'Added to bag'}</span>
    </div>`;
    host.classList.remove('is-in');
    void host.offsetWidth;
    host.classList.add('is-in');
    clearTimeout(host._hideTimer);
    host._hideTimer = setTimeout(() => host.classList.remove('is-in'), 2200);
  };

  const updateCartCount = (count) => {
    doc.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = Number(count) === 0;
    });
    bumpCartUi();
  };

  /* AJAX add to cart (product forms with data-ajax-cart) */
  const addToCart = async (form) => {
    const variantInput = form.querySelector('[name="id"], [data-variant-id]');
    const qtyInput = form.querySelector('[name="quantity"]');
    const id = Number(variantInput?.value || 0);
    const quantity = Math.max(1, Number(qtyInput?.value || 1));
    if (!id) throw new Error('Select a variant');

    const res = await fetch(`${shopRoot()}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items: [{ id, quantity }] }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data.description || data.message || 'Could not add to cart';
      throw new Error(msg);
    }
    return data;
  };

  const showCartError = (form, message) => {
    let el = form.querySelector('[data-cart-error]');
    if (!el) {
      el = doc.createElement('p');
      el.className = 'mw-cart-error';
      el.setAttribute('data-cart-error', '');
      el.style.cssText = 'color:#c1121f;font-size:.85rem;margin:.5rem 0 0;';
      form.appendChild(el);
    }
    el.textContent = message;
  };

  doc.querySelectorAll('form[data-ajax-cart]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const btn = form.querySelector('[type="submit"]');
      const sticky = doc.querySelector('[data-sticky-atc-btn]');
      if (btn) btn.disabled = true;
      if (sticky) sticky.disabled = true;
      form.querySelector('[data-cart-error]')?.remove();
      try {
        await addToCart(form);
        if (typeof window.fbq === 'function') {
          const id = form.querySelector('[name="id"], [data-variant-id]')?.value;
          window.fbq('track', 'AddToCart', { content_ids: [id], content_type: 'product' });
        }
        if (typeof window.gtag === 'function' && window.MW_ADS_ID) {
          window.gtag('event', 'add_to_cart', { send_to: window.MW_ADS_ID });
        }
        const cartRes = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartRes.json();
        updateCartCount(cart.item_count);
        showBagToast('Added to bag');
        if (doc.body.dataset.cartType === 'drawer') {
          await refreshCartDrawer();
          openDrawer('CartDrawer');
          doc.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        } else {
          window.location.href = `${shopRoot()}cart`;
        }
      } catch (err) {
        showCartError(form, err?.message || 'Could not add to cart');
      } finally {
        if (btn) btn.disabled = false;
        if (sticky) sticky.disabled = false;
      }
    });
  });

  window.MotowheelerDrawer = { open: openDrawer, close: closeDrawers };
  window.Motowheeler = {
    openDrawer,
    closeDrawers,
    updateCartCount,
    refreshCartDrawer,
    /** Call after Shopify theme install — opens cart drawer */
    openCart() {
      openDrawer('CartDrawer');
    },
  };
  /* Demo HTML: prefer drawer checkout */
  if (!body.dataset.cartType) body.dataset.cartType = 'drawer';

  /* Coupon / discount code */
  doc.addEventListener('click', async (e) => {
    const applyBtn = e.target.closest('[data-coupon-apply]');
    if (!applyBtn) return;
    e.preventDefault();
    const wrap = applyBtn.closest('[data-coupon]');
    const input = wrap?.querySelector('[data-coupon-input]');
    const msg = wrap?.querySelector('[data-coupon-msg]');
    const code = (input?.value || '').trim();
    if (!code) {
      if (msg) {
        msg.hidden = false;
        msg.textContent = 'Enter a coupon code';
        msg.classList.add('is-error');
      }
      return;
    }
    applyBtn.disabled = true;
    if (msg) {
      msg.hidden = false;
      msg.textContent = 'Applying…';
      msg.classList.remove('is-error');
    }
    try {
      const url = `${shopRoot()}discount/${encodeURIComponent(code)}?redirect=/cart`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (body.dataset.cartType === 'drawer' && doc.getElementById('CartDrawer')) {
        await refreshCartDrawer(true);
        if (msg) {
          msg.textContent = 'Coupon applied (if valid)';
          msg.classList.remove('is-error');
        }
        wrap?.classList.add('is-coupon-ok');
        setTimeout(() => wrap?.classList.remove('is-coupon-ok'), 1600);
      } else {
        window.location.href = `${shopRoot()}cart`;
        return;
      }
    } catch {
      if (msg) {
        msg.textContent = 'Could not apply code — try again at checkout';
        msg.classList.add('is-error');
      }
    } finally {
      applyBtn.disabled = false;
    }
  });

  doc.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('[data-coupon-input]');
    if (!input) return;
    e.preventDefault();
    input.closest('[data-coupon]')?.querySelector('[data-coupon-apply]')?.click();
  });

  /* Gear category scroll progress (Swipe sideways · tap a circle) */
  doc.querySelectorAll('[data-gear-scroll]').forEach((wrap) => {
    const list = wrap.querySelector('[data-gear-list]');
    const fill = wrap.querySelector('[data-gear-fill]');
    if (!list) return;

    const updateFill = () => {
      const max = list.scrollWidth - list.clientWidth;
      const pct = max <= 0 ? 100 : Math.min(100, (list.scrollLeft / max) * 65 + 35);
      if (fill) fill.style.width = `${pct}%`;
    };

    list.addEventListener('scroll', updateFill, { passive: true });
    window.addEventListener('resize', updateFill, { passive: true });
    updateFill();
  });

  /* Product card click fallback — whole card opens product (photo / empty space) */
  doc.addEventListener('click', (e) => {
    if (e.defaultPrevented) return;
    if (e.target.closest('a, button, input, label, [data-wishlist-toggle], [data-quick-add]')) return;
    const card = e.target.closest('.product-card[data-product-url]');
    if (!card) return;
    const url = card.getAttribute('data-product-url');
    if (url) window.location.href = url;
  });

  /* Quick add from product cards */
  doc.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.disabled || btn.classList.contains('is-loading')) return;
    const id = Number(btn.getAttribute('data-variant-id') || 0);
    if (!id) return;
    const label = btn.textContent;
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.textContent = 'Adding…';
    try {
      const res = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add');
      const cartRes = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      updateCartCount(cart.item_count);
      showBagToast('Added to bag');
      btn.classList.remove('is-loading');
      btn.classList.add('is-done');
      btn.textContent = 'Added ✓';
      if (doc.body.dataset.cartType === 'drawer') {
        await refreshCartDrawer();
        openDrawer('CartDrawer');
        doc.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      }
      setTimeout(() => {
        btn.classList.remove('is-done');
        btn.textContent = label;
        btn.disabled = false;
      }, 1600);
    } catch (err) {
      btn.classList.remove('is-loading');
      btn.textContent = 'Try again';
      btn.disabled = false;
      console.warn(err);
      setTimeout(() => {
        btn.textContent = label;
      }, 1800);
    }
  });

  /* Horizontal rails: distinguish tap vs swipe so product opens on tap */
  doc.querySelectorAll('.product-rail').forEach((rail) => {
    let startX = 0;
    let startY = 0;
    let moved = false;
    rail.addEventListener(
      'pointerdown',
      (e) => {
        startX = e.clientX;
        startY = e.clientY;
        moved = false;
      },
      { passive: true }
    );
    rail.addEventListener(
      'pointermove',
      (e) => {
        if (Math.abs(e.clientX - startX) > 10 || Math.abs(e.clientY - startY) > 10) moved = true;
      },
      { passive: true }
    );
    rail.addEventListener('click', (e) => {
      if (!moved) return;
      const link = e.target.closest('a.product-card__media, .product-card__title a');
      if (link) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  });

  /* Product image skeleton — overlay only, no size change */
  const bindSkeleton = (rootEl) => {
    (rootEl || doc).querySelectorAll('.product-card__media').forEach((media) => {
      if (media.dataset.skeletonBound) return;
      media.dataset.skeletonBound = '1';
      const img = media.querySelector('img.product-card__image, img');
      if (!img) return;
      if (img.complete && img.naturalWidth > 0) return;
      media.classList.add('is-skeleton');
      const done = () => media.classList.remove('is-skeleton');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  };
  bindSkeleton();

  /* Trusted brands marquee — clone chips, keep fixed 78×44 / logo ≤64×22 */
  doc.querySelectorAll('[data-brand-marquee]').forEach((viewport) => {
    const track = viewport.querySelector('.brand-strip__track');
    if (!track || track.dataset.marqueeReady) return;
    const items = [...track.children];
    if (items.length < 2) return;
    track.dataset.marqueeReady = '1';
    items.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.tabIndex = -1;
      track.appendChild(clone);
    });
  });

  /* Hazard: brief speed boost on click / tap */
  doc.querySelectorAll('.hazard-stripe').forEach((stripe) => {
    stripe.addEventListener('click', () => {
      stripe.classList.add('is-fast');
      setTimeout(() => stripe.classList.remove('is-fast'), 1200);
    });
  });

  /* Instagram / Google review rails — prev/next + drag scroll */
  const bindRailNav = (railSel, prevSel, nextSel) => {
    doc.querySelectorAll(railSel).forEach((rail) => {
      const wrap = rail.closest('.mw-ig__rail-wrap, .mw-grev__rail-wrap') || rail.parentElement;
      const prev = wrap?.querySelector(prevSel);
      const next = wrap?.querySelector(nextSel);
      const step = () => Math.max(180, Math.floor(rail.clientWidth * 0.8));
      prev?.addEventListener('click', (e) => {
        e.preventDefault();
        rail.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      next?.addEventListener('click', (e) => {
        e.preventDefault();
        rail.scrollBy({ left: step(), behavior: 'smooth' });
      });

      let down = false;
      let startX = 0;
      let startLeft = 0;
      rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        down = true;
        startX = e.clientX;
        startLeft = rail.scrollLeft;
        rail.setPointerCapture?.(e.pointerId);
      });
      rail.addEventListener('pointermove', (e) => {
        if (!down) return;
        rail.scrollLeft = startLeft - (e.clientX - startX);
      });
      const end = () => {
        down = false;
      };
      rail.addEventListener('pointerup', end);
      rail.addEventListener('pointercancel', end);
    });
  };
  bindRailNav('[data-ig-rail]', '[data-ig-prev]', '[data-ig-next]');
  bindRailNav('[data-grev-rail]', '[data-grev-prev]', '[data-grev-next]');
})();
